<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="dialogStore.confirmDialog.isOpen"
        class="modal-overlay modal-overlay-dialog"
        @click.self="handleOverlayClick"
      >
        <div class="modal-dialog">
          <div class="modal-header modal-header-compact">
            <h3>{{ dialogStore.confirmDialog.options.title || 'Confirm' }}</h3>
          </div>
          <div class="modal-body">
            <p>{{ dialogStore.confirmDialog.options.message }}</p>
          </div>
          <div class="modal-footer">
            <button
              v-if="!dialogStore.confirmDialog.options.hideCancelButton"
              class="modal-button modal-button-secondary"
              @click="handleCancel"
            >
              {{ dialogStore.confirmDialog.options.cancelText || 'Cancel' }}
            </button>
            <button
              class="modal-button modal-button-primary"
              autofocus
              @click="handleConfirm"
            >
              {{ dialogStore.confirmDialog.options.confirmText || 'Confirm' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import { useDialogStore } from '../stores/dialogs';

const dialogStore = useDialogStore();

const handleConfirm = (): void => {
  dialogStore.resolveConfirm(true);
};

const handleCancel = (): void => {
  dialogStore.resolveConfirm(false);
};

const handleOverlayClick = (): void => {
  // If Cancel button is hidden (alert mode), treat overlay click as confirm
  if (dialogStore.confirmDialog.options.hideCancelButton) {
    handleConfirm();
  } else {
    handleCancel();
  }
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!dialogStore.confirmDialog.isOpen) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    // If Cancel button is hidden (alert mode), treat ESC as confirm
    if (dialogStore.confirmDialog.options.hideCancelButton) {
      handleConfirm();
    } else {
      handleCancel();
    }
  } else if (event.key === 'Enter') {
    event.preventDefault();
    handleConfirm();
  }
};

// Set up keyboard listeners when dialog opens/closes
watch(() => dialogStore.confirmDialog.isOpen, (isOpen) => {
  if (isOpen) {
    window.addEventListener('keydown', handleKeydown);
  } else {
    window.removeEventListener('keydown', handleKeydown);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
/* Modal dialog now uses global .modal-dialog utility class */
/* Modal structure now uses global utilities (modal-header-compact, modal-body, modal-footer) */
/* Modal buttons now use global .modal-button utility classes */
/* Modal transitions now use global .modal-enter-active/.modal-leave-active classes */
</style>
